import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { arrayOf, shape, string, number, object, bool } from 'prop-types';

import useMenuStore from 'stores/menu';
import useGameStore, { ACTIVE_GAME } from 'stores/game';
import useBreakpoint, { DESKTOP, TABLET_LANDSCAPE } from 'hooks/useBreakpoint';
import useLayoutStore from 'stores/layout';

import DragScroll from 'components/DragScroll/DragScroll';
import BingoCard from 'components/BingoCard/BingoCard';
import LastDrawnBalls from 'components/GameInfoPanel/LastDrawnBalls/LastDrawnBalls';
import ListOfPrizes from 'components/GameInfoPanel/ListOfPrizes/ListOfPrizes';

import styles from './LayoutDefaultSmall.module.scss';

const gameStateSelector = (state) => state.gameState;
const menuExpandedSelector = (state) => state.isMenuExpanded;
function LayoutDefaultSmall({ items, layoutId, isTinyTickets }) {
  const [firstCard, secondCard, thirdCard, ...restCards] = items;
  const gameState = useGameStore(gameStateSelector);
  const is4x4Tickets = useLayoutStore((state) => state.is4x4Tickets);
  const breakpoint = useBreakpoint();
  const isMenuExpanded = useMenuStore(menuExpandedSelector);
  const cards = layoutId === 1 || layoutId === 0 ? items : restCards;

  const useWindowInnerHeight = () => {
    const [windowInnerHeight, setWindowInnerHeight] = useState(() => {
      if (typeof window !== 'undefined') {
        return window.innerHeight;
      }
    });
    useEffect(() => {
      setWindowInnerHeight(window.innerHeight);
      const onResize = () => {
        setWindowInnerHeight(window.innerHeight);
      };
      window.addEventListener('resize', onResize);

      return () => window.removeEventListener('resize', onResize);
    }, []);

    return windowInnerHeight;
  };

  const innerHeight = useWindowInnerHeight();

  return (
    <>
      <div
        className={clsx(styles.miniCard, {
          [styles.isVisible]: layoutId === 0 && gameState === ACTIVE_GAME,
        })}
      >
        {firstCard && (
          <span className={styles.card}>
            <BingoCard
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...firstCard}
              hasFooter
            />
          </span>
        )}
        <LastDrawnBalls isVertical />
        <div className={styles.prizes}>
          <ListOfPrizes isVertical />
        </div>
      </div>
      <DragScroll
        classNameWrapper={styles.dragScrollWrapper}
        classNameScrollbar={clsx(styles.scrollbar, {
          [styles.isHidden]: layoutId === 0 && gameState === ACTIVE_GAME,
        })}
        forceUpdate={layoutId}
      >
        <div
          className={clsx(styles.wrapper, {
            [styles.isHidden]: layoutId === 0 && gameState === ACTIVE_GAME,
            [styles.isLandscape]: breakpoint === TABLET_LANDSCAPE,
            [styles.isOneTicket]: !secondCard,
            [styles.isMenuExpanded]: isMenuExpanded,
            [styles.isActiveGame]: gameState === ACTIVE_GAME,
            [styles.isGrid]: layoutId === 2,
            [styles.isTinyTickets]: isTinyTickets,
            [styles.is4x4Tickets]: is4x4Tickets,
          })}
        >
          <div
            className={clsx(styles.innerWrapper, {
              [styles.isColumn]: layoutId === 3,
            })}
          >
            {layoutId !== 1 && layoutId !== 0 && (
              <div
                className={clsx(styles.firstRow, {
                  [styles.isColumn]: layoutId === 1 || layoutId === 2,
                })}
              >
                {firstCard && (
                  <div key={firstCard.id} className={clsx(styles.item)}>
                    <BingoCard
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...firstCard}
                      isLargeTinyTicket
                      hasFooter
                    />
                  </div>
                )}
                {secondCard && (
                  <div key={secondCard.id} className={clsx(styles.item)}>
                    <BingoCard
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...secondCard}
                      isLargeTinyTicket
                      hasFooter
                    />
                  </div>
                )}
                {thirdCard && (
                  <div
                    key={thirdCard.id}
                    className={clsx(styles.item, {
                      [styles.isHidden]:
                        (items.length < 14 && layoutId === 3) ||
                        (layoutId === 2 &&
                          innerHeight < 1024 &&
                          breakpoint === DESKTOP),
                    })}
                  >
                    <BingoCard
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...thirdCard}
                      isLargeTinyTicket
                      hasFooter
                    />
                  </div>
                )}
              </div>
            )}
            <div
              className={clsx(styles.row, {
                [styles.isGridSixRows]: layoutId === 2,
                [styles.isGrid]: layoutId === 3,
                [styles.isList]: layoutId === 1 || layoutId === 0,
                [styles.isMenuExpanded]: isMenuExpanded,
                [styles.isActiveGame]: gameState === ACTIVE_GAME,
                [styles.isTinyTickets]: isTinyTickets,
              })}
            >
              {(thirdCard && layoutId === 3 && items.length < 14) ||
                (thirdCard &&
                  layoutId === 2 &&
                  innerHeight < 1024 &&
                  breakpoint === DESKTOP && (
                    <div key={thirdCard.id} className={clsx(styles.item)}>
                      <BingoCard
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...thirdCard}
                        isLargeTinyTicket
                        hasFooter
                      />
                    </div>
                  ))}
              {cards.map((card) => (
                <div key={card.id} className={styles.item}>
                  <BingoCard
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...card}
                    isLarge={layoutId === 1 || layoutId === 0}
                    hasFooter
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </DragScroll>
    </>
  );
}

LayoutDefaultSmall.propTypes = {
  items: arrayOf(
    shape({
      id: string.isRequired,
      numbersMap: arrayOf(arrayOf(object)),
    })
  ),
  layoutId: number,
  isTinyTickets: bool,
};

LayoutDefaultSmall.defaultProps = {
  items: [],
  layoutId: 1,
  isTinyTickets: false,
};

export default LayoutDefaultSmall;
